const DANGEROUS_PASTE_TAGS = new Set([
  'audio',
  'canvas',
  'embed',
  'iframe',
  'img',
  'link',
  'math',
  'meta',
  'object',
  'script',
  'style',
  'svg',
  'video',
])

const LIST_TAGS = new Set(['ol', 'ul'])
const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])

const DEFAULT_FORMATTING = {
  bold: false,
  italic: false,
  underline: false,
}

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

export const plainTextToPastedHtml = (text) => escapeHtml(text)
  .replace(/\r\n?/g, '\n')
  .split('\n')
  .map((line) => line || '<br>')
  .join('<br>')

const fontWeightFromStyle = (element) => (
  (element.getAttribute('style') || '').match(/font-weight\s*:\s*([^;]+)/i)?.[1]?.trim().toLowerCase()
)

const fontStyleFromStyle = (element) => (
  (element.getAttribute('style') || '').match(/font-style\s*:\s*([^;]+)/i)?.[1]?.trim().toLowerCase()
)

const textDecorationFromStyle = (element) => (
  (element.getAttribute('style') || '').match(/text-decoration(?:-line)?\s*:\s*([^;]+)/i)?.[1]?.trim().toLowerCase()
)

const parsePastedFormatting = (element, inheritedFormatting) => {
  const tagName = element.tagName.toLowerCase()
  const fontWeight = fontWeightFromStyle(element)
  const fontStyle = fontStyleFromStyle(element)
  const textDecoration = textDecorationFromStyle(element)
  let bold = inheritedFormatting.bold || tagName === 'b' || tagName === 'strong' || HEADING_TAGS.has(tagName)
  let italic = inheritedFormatting.italic || tagName === 'i' || tagName === 'em'
  let underline = inheritedFormatting.underline || tagName === 'u' || tagName === 'ins'

  if (fontWeight) {
    bold = fontWeight === 'bold' || Number.parseInt(fontWeight, 10) >= 600
  }

  if (fontStyle) {
    italic = fontStyle === 'italic' || fontStyle === 'oblique'
  }

  if (textDecoration) {
    underline = textDecoration.includes('underline') && !textDecoration.includes('none')
  }

  return { bold, italic, underline }
}

const wrapSanitizedNode = (tagName, node) => {
  const wrapper = document.createElement(tagName)
  wrapper.appendChild(node)
  return wrapper
}

const applyPastedFormatting = (node, formatting) => {
  let formattedNode = node

  if (formatting.underline) {
    formattedNode = wrapSanitizedNode('u', formattedNode)
  }

  if (formatting.italic) {
    formattedNode = wrapSanitizedNode('em', formattedNode)
  }

  if (formatting.bold) {
    formattedNode = wrapSanitizedNode('strong', formattedNode)
  }

  return formattedNode
}

const appendSanitizedPasteChildren = (source, parent, formatting, options = {}) => {
  Array.from(source.childNodes).forEach((child) => {
    appendSanitizedPasteNode(child, parent, formatting, options)
  })
}

const hasRenderedContent = (node) => (
  Array.from(node.childNodes).some((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      return Boolean((child.textContent || '').replace(/\u200B/g, '').trim())
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      return false
    }

    return child.tagName.toLowerCase() !== 'br' && hasRenderedContent(child)
  })
)

const appendListItemBlock = (source, listItem, formatting) => {
  if (hasRenderedContent(listItem)) {
    listItem.appendChild(document.createElement('br'))
  }

  appendSanitizedPasteChildren(source, listItem, formatting, { insideListItem: true })
}

const appendSanitizedPasteNode = (node, parent, formatting = DEFAULT_FORMATTING, options = {}) => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent || '').replace(/\u200B/g, '')

    if (!text.trim() && /[\n\r\t]/.test(text)) {
      return
    }

    const textNode = document.createTextNode(text)
    parent.appendChild(applyPastedFormatting(textNode, formatting))
    return
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return
  }

  const element = node
  const tagName = element.tagName.toLowerCase()

  if (DANGEROUS_PASTE_TAGS.has(tagName)) {
    return
  }

  if (tagName === 'br') {
    parent.appendChild(document.createElement('br'))
    return
  }

  const nextFormatting = parsePastedFormatting(element, formatting)

  if (LIST_TAGS.has(tagName)) {
    const list = document.createElement(tagName)
    appendSanitizedPasteChildren(element, list, nextFormatting)
    if (hasRenderedContent(list)) {
      parent.appendChild(list)
    }
    return
  }

  if (tagName === 'li') {
    const listItem = document.createElement('li')
    appendSanitizedPasteChildren(element, listItem, nextFormatting, { insideListItem: true })
    if (hasRenderedContent(listItem)) {
      parent.appendChild(listItem)
    }
    return
  }

  if (HEADING_TAGS.has(tagName)) {
    const block = document.createElement('div')
    appendSanitizedPasteChildren(element, block, nextFormatting)
    parent.appendChild(block)
    return
  }

  if (tagName === 'p' || tagName === 'div') {
    if (options.insideListItem) {
      appendListItemBlock(element, parent, nextFormatting)
      return
    }

    const block = document.createElement('div')
    appendSanitizedPasteChildren(element, block, nextFormatting)
    parent.appendChild(block)
    return
  }

  appendSanitizedPasteChildren(element, parent, nextFormatting, options)
}

export const sanitizePastedHtml = (html) => {
  if (!html || typeof document === 'undefined') {
    return ''
  }

  const template = document.createElement('template')
  template.innerHTML = html

  const container = document.createElement('div')
  Array.from(template.content.childNodes).forEach((node) => {
    appendSanitizedPasteNode(node, container)
  })

  return container.innerHTML
}
